import type { ReactNode } from "react";
import { C, FONT, logoDot } from "../theme";

export type LegalDoc = "terms" | "privacy" | "imprint";

const link = { color: C.ac, textDecoration: "underline" } as const;

function H({ children }: { children: ReactNode }) {
  return <h2 style={{ color: C.textBrightest, fontSize: 17, fontWeight: 700, margin: "32px 0 10px" }}>{children}</h2>;
}
function P({ children }: { children: ReactNode }) {
  return <p style={{ color: C.mutedSoft, fontSize: 14, lineHeight: 1.8, margin: "0 0 12px" }}>{children}</p>;
}
function UL({ items }: { items: ReactNode[] }) {
  return (
    <ul style={{ color: C.mutedSoft, fontSize: 14, lineHeight: 1.8, margin: "0 0 12px", paddingLeft: 20 }}>
      {items.map((it, i) => (
        <li key={i} style={{ marginBottom: 6 }}>{it}</li>
      ))}
    </ul>
  );
}

const TITLES: Record<LegalDoc, string> = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  imprint: "Imprint",
};

const OSM = (
  <a href="https://www.openstreetmap.org/copyright" style={link}>© OpenStreetMap contributors</a>
);

export function Legal({ doc }: { doc: LegalDoc }) {
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: FONT }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 32px", borderBottom: `1px solid ${C.line}`, position: "sticky", top: 0, background: C.bg, zIndex: 10 }}>
        <a href="/" style={{ display: "flex", alignItems: "center", gap: 11, color: C.textBright, fontWeight: 700, fontSize: 15, textDecoration: "none" }}>
          <span style={logoDot} />geo.zynday.com
        </a>
        <a href="/" style={{ color: C.muted, fontSize: 13, textDecoration: "underline" }}>← back to site</a>
      </header>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 32px 80px" }}>
        <h1 style={{ color: C.textBrightest, fontSize: 30, fontWeight: 700, margin: "0 0 6px" }}>{TITLES[doc]}</h1>
        <div style={{ color: C.mutedDim, fontSize: 12, marginBottom: 8 }}>geo.zynday.com · effective 2026-06-18</div>
        {doc === "terms" && <Terms />}
        {doc === "privacy" && <Privacy />}
        {doc === "imprint" && <Imprint />}
      </div>

      <footer style={{ borderTop: `1px solid ${C.line}`, padding: "24px 32px", display: "flex", gap: 18, justifyContent: "center", color: C.mutedDim, fontSize: 12 }}>
        <a href="/terms" style={link}>Terms</a>
        <a href="/privacy" style={link}>Privacy</a>
        <a href="/imprint" style={link}>Imprint</a>
        <span>· data {OSM}</span>
      </footer>
    </div>
  );
}

function Provider() {
  return (
    <P>
      <strong style={{ color: C.text }}>zynday UG (haftungsbeschränkt)</strong><br />
      Martinistraße 62–66, 28195 Bremen, Germany<br />
      Managing Director: Ioannis Karagiannidis<br />
      Phone: +49 421 989 98 512 · Email: <a href="mailto:contact@zynday.com" style={link}>contact@zynday.com</a><br />
      Commercial register: HRB&nbsp;<span style={{ color: C.mutedDim }}>[to be added]</span>
    </P>
  );
}

function Terms() {
  return (
    <>
      <P>These Terms govern use of the geo.zynday.com geocoding API and customer portal (the “Service”), operated by zynday UG (haftungsbeschränkt) — see the <a href="/imprint" style={link}>Imprint</a>. They apply to business customers (B2B); the Service is not directed at consumers. By subscribing or using an API key, you accept these Terms.</P>

      <H>1. Service</H>
      <P>The Service resolves addresses to coordinates and coordinates to addresses (“lookups”) over HTTPS, using data derived from OpenStreetMap (see §6). It is offered on a flat subscription with fair-use rate limiting (up to ~20 requests per second per account; no hard monthly request cap). Availability is best-effort; no specific uptime is guaranteed unless separately agreed.</P>

      <H>2. Registration and access</H>
      <UL items={[
        "Login is passwordless via a one-time code sent to your email.",
        "You may create up to five API keys. Each key must be restricted to one or more allowed domains.",
        "Treat keys like passwords; you are responsible for all use under your keys until revoked.",
        "We may suspend keys or accounts for breach of these Terms.",
      ]} />

      <H>3. Subscription term, pricing and payment</H>
      <P>Plans are monthly, 1-year, 2-year or 3-year, billed up front for the chosen term in EUR via Stripe. Subscriptions renew automatically unless cancelled before the renewal date; cancellation takes effect at the end of the current term. The price for your term is fixed for that term; we may change prices for future terms with notice. Manage or cancel anytime via the billing portal.</P>

      <H>4. Acceptable use</H>
      <P>You agree not to:</P>
      <UL items={[
        "exceed the fair-use rate limits or circumvent rate limiting;",
        "resell or sublicense raw API access as a competing geocoding API;",
        <>systematically query the Service to reconstruct, or build a substantial part of, the OpenStreetMap database — including bulk harvesting, exhaustive crawling, or accumulating results into a derivative geospatial database covering substantially the same scope as OpenStreetMap (see §6);</>,
        "use the Service unlawfully or to infringe third-party rights.",
      ]} />
      <P>You may cache and store individual lookup results needed for your application (these are insubstantial extracts).</P>

      <H>5. Changes to these Terms</H>
      <P>We may amend these Terms with reasonable notice (e.g. by email or in the portal). Continued use after the effective date constitutes acceptance.</P>

      <H>6. Data, licensing and attribution (OpenStreetMap)</H>
      <P>The Service returns data derived from OpenStreetMap, licensed under the Open Database License (ODbL) 1.0. Individual lookup results are insubstantial extracts (not a Derivative Database).</P>
      <P><strong style={{ color: C.text }}>Attribution is mandatory at both levels:</strong></P>
      <UL items={[
        <>We credit {OSM} on the site and in API documentation.</>,
        <>You must pass through the same credit — display “© OpenStreetMap contributors” (linked to openstreetmap.org/copyright) wherever you show or use lookup results in your own product. This is a condition of the ODbL and of these Terms.</>,
      ]} />
      <P>If you breach §4 and create a Derivative Database, ODbL Share-Alike obligations attach to you, and we may terminate your access.</P>

      <H>7. Liability</H>
      <P>To the extent permitted by law, zynday UG is liable without limitation only for intent and gross negligence, and for injury to life, body or health. For slight negligence we are liable only for breach of a material contractual obligation (cardinal duty), limited to foreseeable, contract-typical damage. The Service and its data are provided “as is”; we do not warrant the accuracy or completeness of geocoding results.</P>

      <H>8. Data protection</H>
      <P>We process personal data as described in our <a href="/privacy" style={link}>Privacy Policy</a>. Payment is handled by Stripe.</P>

      <H>9. Governing law and jurisdiction</H>
      <P>German law applies, excluding the UN Convention on Contracts for the International Sale of Goods (CISG). The exclusive place of jurisdiction, where permitted, is Bremen, Germany.</P>
    </>
  );
}

function Privacy() {
  return (
    <>
      <H>1. Controller</H>
      <Provider />

      <H>2. Purposes of processing</H>
      <P>We process data to provide the geocoding API and portal: account login, issuing and validating API keys, enforcing domain allowlists and rate limits, metering usage, billing, and fraud/abuse prevention.</P>

      <H>3. Legal bases</H>
      <UL items={[
        "Art. 6(1)(b) GDPR — performance of the contract (providing the Service, billing).",
        "Art. 6(1)(f) GDPR — legitimate interests (security, abuse prevention, rate limiting).",
        "Art. 6(1)(c) GDPR — legal obligations (e.g. retention of invoices).",
      ]} />

      <H>4. Categories of data</H>
      <UL items={[
        "Account: email address; one-time login codes; session tokens.",
        "API usage: API key identifiers (hashed), allowed domains, request counts, timestamps, and the requesting Origin/Referer and IP for rate limiting and abuse prevention.",
        "Billing: Stripe customer/subscription identifiers and plan status (card data is handled by Stripe, not stored by us).",
        "Lookup queries you send are proxied to our geocoding backend to return results; we do not build profiles from them.",
      ]} />

      <H>5. Payment processing (Stripe)</H>
      <P>Payments are processed by Stripe Payments Europe, Ltd. We receive subscription status and customer identifiers; we never receive full card details. See Stripe’s privacy policy.</P>

      <H>6. Hosting &amp; infrastructure</H>
      <P>The portal and API run on Cloudflare (Workers, Durable Objects). Geocoding is served from a self-hosted backend on a server in the EU. Email delivery uses Cloudflare Email Service (with Brevo as a fallback provider).</P>

      <H>7. International transfers</H>
      <P>Where a processor processes data outside the EU/EEA, transfers are safeguarded by EU Standard Contractual Clauses or an adequacy decision.</P>

      <H>8. Retention</H>
      <P>Account and usage data are kept for the life of the account and deleted on account deletion, except where statutory retention (e.g. invoices) applies. One-time codes and sessions expire automatically.</P>

      <H>9. Your rights</H>
      <P>You have the right to access, rectification, erasure, restriction, data portability, and objection. Contact <a href="mailto:contact@zynday.com" style={link}>contact@zynday.com</a>. You may delete your account (and all keys + data) from the dashboard at any time.</P>

      <H>10. Right to complain</H>
      <P>You may lodge a complaint with the supervisory authority: Die Landesbeauftragte für Datenschutz und Informationsfreiheit der Freien Hansestadt Bremen, Arndtstraße 1, 27570 Bremerhaven.</P>

      <H>11. Cookies &amp; tracking</H>
      <P>We use a single strictly-necessary cookie, <code style={{ color: C.ac }}>geo_session</code> (HttpOnly), to keep you logged in — no consent is required for it. We do not use advertising or third-party tracking cookies. If analytics are introduced in future, we will request consent first.</P>

      <H>12. Data security</H>
      <P>API keys and session/login tokens are stored only as salted hashes; transport is HTTPS only.</P>

      <H>13. Changes</H>
      <P>We may update this policy; the effective date above reflects the latest version.</P>
    </>
  );
}

function Imprint() {
  return (
    <>
      <H>Information pursuant to § 5 TMG</H>
      <Provider />

      <H>VAT</H>
      <P>Exempt under § 19 UStG (Kleinunternehmerregelung); no VAT is shown.</P>

      <H>Responsible for content (§ 18 Abs. 2 MStV)</H>
      <P>John Michael S Drake Brockman, address as above.</P>

      <H>EU dispute resolution</H>
      <P>The European Commission provides a platform for online dispute resolution: <a href="https://ec.europa.eu/consumers/odr" style={link}>ec.europa.eu/consumers/odr</a>. We are not obliged and not willing to participate in dispute-resolution proceedings before a consumer arbitration board.</P>

      <H>Attribution</H>
      <P>Geocoding data is derived from OpenStreetMap, {OSM}, licensed under ODbL 1.0.</P>
    </>
  );
}
